/**
 * Helg-/helligdagsutledning + lønnsgrunnlag (VIB-1224).
 *
 * INGEN kroneberegning (Erik 15.07): systemet leverer kun GRUNNLAGET —
 * føringer på lørdag/søndag/helligdag med dato, og overnattinger med dato
 * (VIB-1287). Lønn kjører satsene selv utenfor systemet.
 *
 * Helligdager kommer fra `company_holidays` (samme kilde som VIB-916-
 * kapasitetsreduksjonen). Datoer sammenlignes som `yyyy-MM-dd`-strenger;
 * ukedag beregnes i lokal tid (brukerne er i Norge — samme antakelse som
 * føringsflatene som lager `start_date`).
 *
 * Pure — no Supabase/React imports.
 */

export type DayKind = "weekday" | "saturday" | "sunday" | "holiday";

/** `yyyy-MM-dd` fra en ISO-dato/timestamp, i LOKAL tid for timestamps. */
export function localDateOf(iso: string): string {
  // Rene datostrenger skal ikke innom Date (UTC-parsing kan flytte dagen).
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Klassifiser en dato. Helligdag (fra company_holidays) har forrang over
 * lørdag/søndag, så f.eks. 1. mai på en lørdag telles som helligdag.
 */
export function classifyDay(dateIso: string, holidays: ReadonlySet<string>): DayKind {
  const dateStr = localDateOf(dateIso);
  if (holidays.has(dateStr)) return "holiday";
  // Midt på dagen unngår DST-kanttilfeller ved midnatt.
  const day = new Date(`${dateStr}T12:00:00`).getDay();
  if (day === 6) return "saturday";
  if (day === 0) return "sunday";
  return "weekday";
}

export function isWeekendOrHoliday(dateIso: string, holidays: ReadonlySet<string>): boolean {
  return classifyDay(dateIso, holidays) !== "weekday";
}

/**
 * Auto-forslag for `weekend_holiday_hours`:
 * hele føringens varighet når dagen er helg/helligdag, ellers null (ingen
 * flagging). Brukeren kan alltid overstyre (også til 0) før lagring.
 */
export function suggestWeekendHolidayHours(
  dateIso: string,
  durationSeconds: number,
  holidays: ReadonlySet<string>,
): number | null {
  if (!isWeekendOrHoliday(dateIso, holidays)) return null;
  const hours = Math.round((durationSeconds / 3600) * 100) / 100;
  return hours > 0 ? hours : null;
}

// ── Lønnsgrunnlag (rapporten under /rapporter) ─────────────────────────

export const DAY_KIND_NB: Record<DayKind, string> = {
  weekday: "Hverdag",
  saturday: "Lørdag",
  sunday: "Søndag",
  holiday: "Helligdag",
};

/** Rå time_entry-rad slik lønnsgrunnlaget trenger den. */
export interface PayrollEntry {
  id: string;
  user_id: string | null;
  full_name: string | null;
  start_date: string;
  project_name: string | null;
  task_description: string;
  weekend_holiday_hours: number | null;
  overnight_count: number | null;
}

export interface PayrollBasisLine {
  entry_id: string;
  user_id: string;
  full_name: string;
  /** yyyy-MM-dd (lokal dato for føringen). */
  date: string;
  /** Prosjektnavn (spec-krav: prosjekt i hver grunnlagsrad). */
  project_name: string;
  task_description: string;
  day_kind: DayKind;
  /** Flaggede helg-/helligdagstimer på føringen (0 hvis kun overnatting). */
  weekend_holiday_hours: number;
  /** Antall netter på føringen (0 hvis kun tilleggstimer). */
  overnight_count: number;
}

export interface PayrollBasisEmployee {
  user_id: string;
  full_name: string;
  total_weekend_holiday_hours: number;
  total_overnights: number;
  lines: PayrollBasisLine[];
}

/**
 * Bygger lønnsgrunnlaget: én grunnlagslinje per føring som har flaggede
 * tilleggstimer og/eller overnattinger, gruppert per ansatt med månedssummer.
 * Attribusjon på `start_date` (aldri created_at). Ingen satser/kroner.
 */
export function buildPayrollBasis(
  entries: PayrollEntry[],
  holidays: ReadonlySet<string>,
): PayrollBasisEmployee[] {
  const byUser = new Map<string, PayrollBasisEmployee>();
  for (const e of entries) {
    const hours = Number(e.weekend_holiday_hours ?? 0);
    const nights = Number(e.overnight_count ?? 0);
    if (hours <= 0 && nights <= 0) continue;
    if (!e.user_id) continue;

    const cur = byUser.get(e.user_id) ?? {
      user_id: e.user_id,
      full_name: e.full_name ?? "Ukjent",
      total_weekend_holiday_hours: 0,
      total_overnights: 0,
      lines: [],
    };
    cur.total_weekend_holiday_hours += hours;
    cur.total_overnights += nights;
    cur.lines.push({
      entry_id: e.id,
      user_id: e.user_id,
      full_name: cur.full_name,
      date: localDateOf(e.start_date),
      project_name: e.project_name ?? "—",
      task_description: e.task_description,
      day_kind: classifyDay(e.start_date, holidays),
      weekend_holiday_hours: hours,
      overnight_count: nights,
    });
    byUser.set(e.user_id, cur);
  }

  const round2 = (n: number) => Math.round(n * 100) / 100;
  return [...byUser.values()]
    .map((emp) => ({
      ...emp,
      total_weekend_holiday_hours: round2(emp.total_weekend_holiday_hours),
      lines: emp.lines.sort((a, b) => a.date.localeCompare(b.date)),
    }))
    .sort((a, b) => a.full_name.localeCompare(b.full_name, "nb"));
}
